import {
  Component,
  HostBinding,
  Injector,
  Type,
  ViewContainerRef,
  computed,
  effect,
  inject,
  input,
  model,
  output,
  untracked,
  viewChild,
} from '@angular/core';
import { outputToObservable } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import {
  AutomationActionType,
  AutomationContext,
  IAutomationAction,
  automationActionByContext,
  automationActionTypeOptions,
  createEnumList,
} from '@kinet-open-network/shared/data-access/model';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { Subject, takeUntil } from 'rxjs';
import {
  ACTION_TOKEN,
  ActionComponent,
} from './actions/abstract-action.component';
import { AutomationActionService } from './automation-action.service';

@Component({
  selector: 'kon-automation-action-container',
  imports: [SelectModule, FormsModule, ButtonModule],
  template: `
    <div class="flex items-center gap-2 pl-2">
      <button
        type="button"
        (click)="toggleCollapsed()"
        class="flex items-center gap-2 grow"
      >
        <i
          [class]="collapsed() ? 'pi pi-chevron-right' : 'pi pi-chevron-down'"
        ></i>
        <span class="font-medium text-sm">
          {{ actionLabel() }}
        </span>
      </button>

      <button
        pButton
        type="button"
        icon="pi pi-times"
        class="p-button-danger p-button-text"
        (click)="actionRemove.emit()"
      ></button>
    </div>

    @if (!collapsed()) {
      <div class="mt-2">
        <label for="type" class="block pl-2 text-sm font-medium mb-1">
          Typ akcie
        </label>
        <p-select
          id="type"
          [options]="actionTypeOptions()"
          [filter]="true"
          [ngModel]="type()"
          (ngModelChange)="handleTypeChange($event)"
          optionLabel="label"
          optionValue="value"
          class="w-full"
        ></p-select>
      </div>

      <div class="mt-4">
        <span class="pl-2 text-sm font-medium"> Argumenty akcie </span>
        <div class="flex flex-col gap-2 border rounded-md p-2 mt-1">
          <ng-template #vcr />
        </div>
      </div>
    }
  `,
})
export class AutomationActionContainerComponent {
  readonly #actionService = inject(AutomationActionService);
  readonly #recreateStream = new Subject<void>();

  readonly action = model<Partial<IAutomationAction>>(
    this.#actionService.createAction(),
  );
  readonly index = input(0);
  readonly context = input<AutomationContext>();

  readonly actionRemove = output<void>();
  readonly vcr = viewChild('vcr', { read: ViewContainerRef });
  readonly type = computed(() => this.action().type);
  readonly collapsed = computed(() => this.action().collapsed);
  readonly actionLabel = computed(() => {
    const type = this.action().type;

    return (
      `Akcia #${this.index() + 1}` +
      (type
        ? ` - ${automationActionTypeOptions.get(type?.toString())?.label}`
        : '')
    );
  });
  readonly enabledActions = computed(() => {
    const ctx = this.context();

    if (!ctx) return [];

    return automationActionByContext[ctx];
  });
  readonly actionTypeOptions = computed(() => {
    const enabledActions = this.enabledActions();

    return createEnumList(automationActionTypeOptions).filter((option) =>
      (enabledActions as unknown[]).includes(option.value),
    );
  });

  @HostBinding('class')
  readonly classes = 'flex flex-col p-2 border rounded-lg';

  private actionComponentInstance: ActionComponent<AutomationActionType> | null =
    null;

  handleTypeChange(type: AutomationActionType) {
    if (this.action().type === type) {
      return;
    }

    this.action.update((action) => ({ ...action, type, data: {} }));
  }

  handleActionChange(action: IAutomationAction) {
    this.action.set(action);
  }

  toggleCollapsed() {
    this.action.update((action) => ({
      ...action,
      collapsed: !action.collapsed,
    }));
  }

  constructor() {
    effect(() => {
      const type = this.type();
      const vcr = this.vcr();
      const collapsed = this.collapsed();

      if (!vcr || collapsed) {
        this.actionComponentInstance = null;
        return;
      }

      if (
        this.actionComponentInstance &&
        type === this.actionComponentInstance.action.type
      ) {
        return;
      }

      const component = this.#actionService.getComponentForType(type);

      untracked(() => {
        this.recreateComponent(component, vcr);
      });
    });
  }

  private recreateComponent(
    componentType: Type<ActionComponent<AutomationActionType>>,
    vcr: ViewContainerRef,
  ) {
    vcr.clear();
    this.#recreateStream.next();
    this.actionComponentInstance = null;

    const injector = Injector.create({
      providers: [
        {
          provide: ACTION_TOKEN,
          useValue: this.action(),
        },
      ],
      parent: vcr.injector,
    });

    const componentRef = vcr.createComponent(componentType, { injector });
    const componentInstance = componentRef.instance;

    outputToObservable(componentInstance.actionChange)
      .pipe(takeUntil(this.#recreateStream))
      .subscribe((action) => {
        this.handleActionChange(action);
      });

    this.actionComponentInstance = componentInstance;

    componentRef.changeDetectorRef.detectChanges();
  }
}
